/** Widget Studio → Home tab (mockups/tenant/widget-home.html). */
export const widgetHome = {
  screen: {
    title: "Home screen",
    hint: "Off: the widget opens straight into the chat, as it does today.",
    enable: "Show Home",
    greeting: "Greeting",
    greetingPlaceholder: "Hi there 👋",
    subtitle: "Subtitle",
    subtitlePlaceholder: "Here's how we can help",
    showTo: "Show Home to",
    counter: "{{count}} of {{max}}",
  },
  showTo: {
    everyone: "Everyone",
    new_visitors: "New visitors",
    returning_visitors: "Returning visitors",
  },
  audience: {
    everyone: "Everyone who sees Home",
    new_visitors: "New visitors",
    returning_visitors: "Returning visitors",
  },
  options: {
    title: "Options",
    count: "({{count}} of {{max}})",
    hint: "Use the arrows to reorder. Each option can have its own audience.",
    listLabel: "Home options",
    add: "Add option",
    full: "You can add up to {{max}} options. Remove one to add another.",
    emptyTitle: "No options yet",
    emptyBody: "Add up to {{max}} shortcuts, like booking a tour or asking about tuition. Visitors see them when the widget opens.",
    editLabel: "Edit",
    edit: "Edit {{label}}",
    moveUp: "Move {{label}} up",
    moveDown: "Move {{label}} down",
    remove: "Remove {{label}}",
    moved: "Moved {{label}} to position {{position}} of {{count}}",
    removed: "{{label}} removed",
    saved: "Option saved",
  },
  actions: {
    workflow: "Start workflow",
    booking: "Booking",
    question: "Question",
    link: "Link",
    person: "Person",
  },
  summary: {
    workflow: "Start workflow · {{target}}",
    booking: "Open booking · {{target}}",
    question: "Ask a question · “{{target}}”",
    link: "Open link · {{target}}",
    person: "Talk to a person",
    unavailable: "no longer available",
  },
  dialog: {
    addTitle: "Add option",
    editTitle: "Home option",
    intro: "A shortcut visitors see when the widget opens.",
    icon: "Icon",
    iconHint: "Pick a glyph for this option.",
    label: "Label",
    labelPlaceholder: "e.g. Book a campus tour",
    descriptionLabel: "Description",
    optional: "(optional)",
    descriptionPlaceholder: "Requirements, deadlines and steps",
    whenTapped: "When tapped",
    workflow: "Workflow",
    workflowPick: "Choose a workflow",
    workflowHint: "Only live workflows with the “Visitor picks a Home option” trigger are listed.",
    workflowNone: "No live workflow uses the “Visitor picks a Home option” trigger yet.",
    workflowCreate: "Create one in Workflows",
    workflowLoading: "Loading workflows…",
    workflowLoadError: "Workflows couldn't load. You may not have access to them.",
    unavailable: "Unavailable workflow or action",
    booking: "Booking action",
    bookingPick: "Choose a booking action",
    bookingHint: "Opens this agent's booking card with open slots.",
    bookingNone: "This agent has no booking action yet. Create one in Actions.",
    bookingCreate: "Create one in Actions",
    bookingLoading: "Loading booking actions…",
    bookingLoadError: "Booking actions couldn't load. You may not have access to them.",
    question: "Question",
    questionHint: "Sent as the visitor's message. The assistant answers it from your knowledge sources.",
    link: "Link",
    linkHint: "Opens in a new tab. Only https:// links work.",
    personHint: "Starts a handoff to a person, using your routing and office hours.",
    showTo: "Show to",
    cancel: "Cancel",
    save: "Save option",
  },
  errors: {
    labelRequired: "Enter a label.",
    workflowRequired: "Choose a workflow.",
    bookingRequired: "Choose a booking action.",
    questionRequired: "Enter the question.",
    urlInvalid: "Use a full https:// link.",
    tooLong: "That is too long.",
    invalid: "Check this field.",
  },
  icons: {
    "message-circle": "Chat",
    "graduation-cap": "Graduation cap",
    wallet: "Wallet",
    calendar: "Calendar",
    download: "Download",
    headset: "Headset",
    "file-text": "Document",
    info: "Information",
    phone: "Phone",
    mail: "Email",
    "map-pin": "Location",
    star: "Star",
    "life-buoy": "Support",
    "book-open": "Book",
    megaphone: "Announcement",
    globe: "Website",
  },
  messenger: {
    title: "Messenger Get Started menu",
    hint: "Shown in Messenger before the first message, and kept in the menu (☰) afterwards.",
    enable: "Messenger Get Started menu",
    greeting: "Greeting text (shown before “Get Started”)",
    greetingHint: "Up to {{max}} characters. Facebook fills in the first name where you write {{token}}.",
    menu: "Use the Home options as the menu",
    menuHint: "The first {{count}} options become menu items; “Open link” options open in Messenger's browser.",
    note: "Applies to the Facebook Page connected in Channels. Facebook may take a few minutes to show changes.",
    channels: "Open Channels",
    syncPage: "Facebook Page: {{page}}",
    syncedAt: "Last synced {{when}}",
    noPage: "No Facebook Page is connected yet. Connect one in Channels to use the Get Started menu.",
    sync: {
      synced: "Synced with Facebook",
      removed: "Removed from the Page",
      failed: "Facebook did not accept the update",
      skipped: "Not sent to Facebook",
      never: "Not sent to Facebook yet",
    },
  },
  preview: {
    live: "Home preview",
    channel: "Preview channel",
    widget: "Widget",
    messenger: "Messenger",
    off: "Home is off, so the widget opens straight into the chat. Turn it on to show this screen.",
    widgetFrame: "Widget Home preview",
    messengerFrame: "Messenger preview",
    defaultGreeting: "Hi there 👋",
    defaultSubtitle: "Here's how we can help",
    optionsLabel: "Home options preview",
    noOptions: "Your options appear here.",
    questionTitle: "Have another question?",
    questionBody: "Our assistant answers from your knowledge sources.",
    ask: "Ask us anything",
    poweredBy: "Powered by {{product}}",
    privacy: "Privacy",
    typicalReply: "Typically replies instantly",
    getStarted: "Get Started",
    menu: "Menu",
    messengerEmpty: "Turn on Get Started or the menu to see them here.",
    sampleName: "Ana",
    footer: "Preview uses your brand colour and radius from Appearance. Option taps, views and conversions are counted below.",
  },
  stats: {
    title: "How Home performed",
    window: "Last {{days}} days",
    impressions: "Home views",
    conversations: "Conversations started",
    taps: "Taps per option",
    tapCount_one: "{{count}} tap",
    tapCount_other: "{{count}} taps",
    emptyTitle: "No Home views yet",
    emptyBody: "Publish Home and the numbers start counting as visitors open the widget.",
    errorTitle: "Home numbers couldn't load",
    errorBody: "Check your connection and try again.",
    noTaps: "No taps yet",
    removedOption: "{{id}} (removed)",
  },
} as const;
